<template>
        <section class="summary-section cart-page-summary" aria-labelledby="summary-heading">
            <div class="summary-container cart">
                <div class="container-header">
                    <h3 id="summary-heading" class="headline-s">Summary</h3>
                </div>
                <dl>
                    <dt class="paragraph-default" aria-label="Subtotal amount">Subtotal</dt>
                    <dd class="paragraph-default">{{ currencySymbol }}{{ subTotal }}</dd>
                </dl>
                <dl v-if="isPropertyVisible('discounts') && discount > 0">
                    <dt class="paragraph-default" aria-label="Discounts amount">Discounts</dt>
                    <dd class="paragraph-default discount-price">-{{ currencySymbol }}{{ discount }}</dd>
                </dl>
                <dl>
                    <dt class="paragraph-default" aria-label="Taxes status">Taxes</dt>
                    <dd :class="{
                        'paragraph-default': true,
                        'font-italic': true
                    }">{{ isTaxExempt ? 'Tax Exempt' : 'Calculated at Checkout' }}</dd>
                </dl>
                <dl>
                    <dt class="paragraph-default" aria-label="Shipping status">Shipping</dt>
                    <dd class="paragraph-default font-italic">{{ shippingStatusLabel }}</dd>
                </dl>
                <dl class="order-total">
                    <dt class="title-l" aria-label="Estimated total amount">Estimated Total</dt>
                    <dd class="title-l">{{ currencySymbol }}{{ subTotal }}</dd>
                </dl>
                <div class="bnpl-info" v-if="isPropertyVisible('bnlp_message')">
                    <p class="paragraph-details text-rothcoBlack">From $97.79/mo with BNPL</p>
                    <a href="#" class="link-400" aria-label="Learn more about Buy Now, Pay Later">Learn More</a>
                </div>
                <div class="apply-promo-code-section" aria-label="Promo code application information" v-if="isPropertyVisible('apply_promo_later')">
                    <p class="font-italic text-rothcoBlack">You will be able to apply a promo code to your order during checkout.</p>
                </div>

                <div v-if="canPlaceOrders" class="checkout-button mobile-hidden-flex">
                    <Link as="button" :disabled="!allowCheckout"  :href="checkoutRoute" class="primary" aria-label="Proceed to checkout">
                        Checkout
                        <div aria-hidden="true" class="icon">
                            <CarretsBlack alt="carrets right" />
                        </div>
                    </Link>
                </div>

                <ApplyCoupon :couponCode="couponCode" class="mt-6" v-if="isPropertyVisible('apply_coupon')"/>

                <div v-if="canPlaceOrders" class="checkout-button desktop-hidden-flex">
                    <RothcoCta v cta-type="button" :disabled="!allowCheckout" :href="checkoutRoute" class="primary" aria-label="Proceed to checkout">
                        Checkout
                    </RothcoCta>
                </div>
                <a href="#" @click.prevent="handleAccountLogin" class="link-400 dealer-checkout-link" aria-label="Proceed to Dealer Checkout" v-if="isPropertyVisible('dealer_checkout_btn') && !isLoggedIn">Dealer Checkout</a>
                <div  v-if="canPlaceOrders"  class="cards-section" aria-label="Accepted payment methods">
                        <VisaIcon alt="Visa logo"/>
                        <MastercardIcon alt="MasterCard logo"/>
                        <template v-if="isPropertyVisible('show_b2c_cards')">
                            <AmexIcon alt="American Express logo"/>
                            <DiscoverIcon alt="Discover logo"/>
                            <PaypalIcon alt="PayPal logo"/>
                        </template>
                </div>
            </div>
        </section>
</template>

<script lang="ts" setup>
    import { computed, inject } from "vue";
    import { Link } from "@inertiajs/vue3";
    // import { route } from "ziggy-js";
    import { useStorage } from '@vueuse/core';
    import { useSettingsStore } from "@/store/settings";
    import { useCartDetails } from "@/helpers/composables/useCartDetails";
    import { storeToRefs } from "pinia";
    import RothcoCta from "@/components/RothcoCta.vue";
    import ApplyCoupon from "@/components/cart/ApplyCoupon.vue";
    import CarretsBlack from "@icons/carrets-black.svg";
    import VisaIcon from "@icons/visa.svg";
    import MastercardIcon from "@icons/mastercard.svg";
    import AmexIcon from "@icons/amex.svg";
    import DiscoverIcon from "@icons/discover.svg";
    import PaypalIcon from "@icons/paypal.svg";

    let props = defineProps({
        list: {
            type: Object,
            default: ()=>({})
        },
        isTaxExempt: {
            type: Boolean,
            default: false
        }
    });
    let route = inject("route");
    let list = computed(() => {
        return props.list;
    });

    const couponCode = useStorage("coupon",null);

    let { isPropertyVisible, currencySymbol, subTotal } = useCartDetails(null, list, 'summary');

    let discount = computed(() => {
        return (list.discount ?? 0).toFixed(2);
    });

    let settings = useSettingsStore();


    const { toggleFlyout } = settings;

    let { is_dealer,freeShippingProgressPercentage, isLoggedIn, canSeePricing, canPlaceOrders } = storeToRefs(settings);

    let isB2B = computed(() => is_dealer.value);

    let checkoutRoute = computed(() => {
        return is_dealer.value ? route("checkout.express") : route('shop.show');
    });

    const shippingStatusLabel = computed(() => {
        if(!isB2B.value && freeShippingProgressPercentage.value === 100) {
            return "FREE";
        }

        return  "Calculated at Checkout";
    });

    const listItemErrors = inject('listItemErrors');

    const allowCheckout = computed(() => {
        if(isB2B.value) {
            return true
        }

        return Object.keys(listItemErrors).length == 0;
    });

    const handleAccountLogin = () => {
        toggleFlyout();
    };
</script>
